import { useCallback, useEffect, useState } from 'react';
import type { AiCompleteResult, Conversation } from '@ozon-ai/ai/contracts';
import {
  CONVERSATION_STATUS_LABELS,
  DEFAULT_LLM_BASE_URL,
  DEFAULT_LLM_MODEL,
} from '@ozon-ai/ai/contracts';
import type { MaskedSettings } from '@ozon-ai/apigateway/contracts';
import { Button } from '@/components/ui/button.tsx';
import { api } from '../../../api/client';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { useTelegram } from '../../../context/TelegramContext';
import { StatusBadge } from '../../../ui/StatusBadge';
import { SETTINGS_ACCESS } from '../settings-paths';
import {
  Field,
  SettingsErrorAlert,
  SettingsForm,
  SettingsFormCard,
  SettingsHint,
  SettingsStatusLine,
  SettingsSubPageShell,
  SettingsSuccessAlert,
} from '../settings-shared';

interface ServiceStatus {
  llm?: { ok: boolean; error?: string };
}

export function SettingsAiPage() {
  const { webApp } = useTelegram();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [checkReply, setCheckReply] = useState<string | null>(null);
  const [status, setStatus] = useState<ServiceStatus | null>(null);
  const [llmKey, setLlmKey] = useState('');
  const [model, setModel] = useState(DEFAULT_LLM_MODEL);
  const [baseUrl, setBaseUrl] = useState(DEFAULT_LLM_BASE_URL);
  const [conversations, setConversations] = useState<Conversation[]>([]);

  const loadConversations = useCallback(async () => {
    try {
      const items = await api.get<Conversation[]>('/conversations?limit=10');
      setConversations(items || []);
    } catch {
      setConversations([]);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await api.get<MaskedSettings>('/settings');
      if (data) {
        setLlmKey(data.secrets.llm?.apiKey || '');
        setModel(data.secrets.llm?.model || DEFAULT_LLM_MODEL);
        setBaseUrl(data.secrets.llm?.baseUrl || DEFAULT_LLM_BASE_URL);
      }
      try {
        const st = await api.get<ServiceStatus>('/settings/status');
        setStatus(st);
      } catch {
        setStatus(null);
      }
      await loadConversations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить настройки');
    } finally {
      setLoading(false);
    }
  }, [loadConversations]);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveAi() {
    setBusy(true);
    setMessage(null);
    setCheckReply(null);
    try {
      await api.put('/settings/secrets', {
        llm: {
          apiKey: llmKey,
          provider: 'deepseek',
          model: model.trim() || DEFAULT_LLM_MODEL,
          baseUrl: baseUrl.trim() || DEFAULT_LLM_BASE_URL,
        },
      });
      setMessage('Настройки AI сохранены');
      webApp?.HapticFeedback?.notificationOccurred('success');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения');
    } finally {
      setBusy(false);
    }
  }

  async function checkAi() {
    setChecking(true);
    setError(null);
    setMessage(null);
    setCheckReply(null);
    try {
      const result = await api.post<AiCompleteResult>('/ai/complete', {
        slot: 'general',
        userMessage: 'Ответь одним словом: ок',
      });
      setCheckReply(result.reply);
      setMessage('DeepSeek ответил');
      webApp?.HapticFeedback?.notificationOccurred('success');
      await loadConversations();
    } catch (err) {
      webApp?.HapticFeedback?.notificationOccurred('error');
      setError(err instanceof Error ? err.message : 'Ошибка проверки');
      await loadConversations();
    } finally {
      setChecking(false);
    }
  }

  async function removeConversation(id: string) {
    try {
      await api.delete(`/conversations?id=${encodeURIComponent(id)}`);
      await loadConversations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить диалог');
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <SettingsSubPageShell title="AI" backTo={SETTINGS_ACCESS}>
      {error && <SettingsErrorAlert>{error}</SettingsErrorAlert>}
      {message && <SettingsSuccessAlert>{message}</SettingsSuccessAlert>}

      <SettingsFormCard>
        <SettingsStatusLine label="Статус" ok={status?.llm?.ok} error={status?.llm?.error} />
      </SettingsFormCard>

      <SettingsFormCard>
        <SettingsHint>
          DeepSeek API. Ключ в UI показывается как last4. Оставьте маску ****xxxx, если не хотите менять значение.
        </SettingsHint>
        <SettingsForm onSubmit={saveAi} className="mt-4">
          <Field label="API key" id="llmKey" value={llmKey} onChange={setLlmKey} />
          <Field label="Модель" id="llmModel" value={model} onChange={setModel} />
          <Field label="Base URL" id="llmBaseUrl" value={baseUrl} onChange={setBaseUrl} />
          <Button type="submit" className="w-full" disabled={busy}>
            Сохранить
          </Button>
        </SettingsForm>
        <Button
          type="button"
          variant="outline"
          className="mt-3 w-full"
          disabled={checking || busy}
          onClick={() => void checkAi()}
        >
          {checking ? 'Проверка…' : 'Проверить'}
        </Button>
        {checkReply && <SettingsHint className="mt-3">Ответ: {checkReply}</SettingsHint>}
      </SettingsFormCard>

      <SettingsFormCard title="Последние диалоги">
        {conversations.length === 0 ? (
          <SettingsHint>Диалогов пока нет. Нажмите «Проверить», чтобы создать тестовый.</SettingsHint>
        ) : (
          <ul className="flex flex-col gap-4">
            {conversations.map((conversation) => (
              <li key={conversation.id} className="flex flex-col gap-1 border-b border-border/60 pb-3 last:border-0 last:pb-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 flex-1 truncate font-semibold">{conversation.title}</span>
                  <StatusBadge status={conversation.status}>
                    {CONVERSATION_STATUS_LABELS[conversation.status] || conversation.status}
                  </StatusBadge>
                </div>
                <SettingsHint>
                  {new Date(conversation.updatedAt).toLocaleString('ru-RU')} · {conversation.model}
                </SettingsHint>
                {conversation.error && (
                  <p className="text-sm text-destructive">{conversation.error}</p>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-fit px-0"
                  onClick={() => void removeConversation(conversation.id)}
                >
                  Удалить
                </Button>
              </li>
            ))}
          </ul>
        )}
      </SettingsFormCard>
    </SettingsSubPageShell>
  );
}
